import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import SimpleNotification from "components/Notification/SimpleNotification";
import { getPlugins } from "components/PluginsPopover/Plugin.selectors";
import PluginListAction from "components/PluginsPopover/PluginListAction";
import { singleCustomObjectProvisioningStateSelector } from "components/ProvisioningStateDot/ProvisioningState.selectors";
import Tooltip from "components/Tooltip/Tooltip";
import { FeConfigType } from "config/config";
import { getIsReadonly, getRecordConfig } from "config/resourceUtils";
import { getUserData, getUserPermissionData } from "containers/Auth/Auth.selectors";
import { hasCreateUserPermission } from "containers/Auth/permissions";
import { BundleState, logScreenId } from "containers/BundleManager/BundleManager";
import { getBundlesData } from "containers/BundleManager/Bundles.selectors";
import { BundleResponseData } from "containers/BundleManager/interfaces";
import { getActiveOrDefaultProjectId } from "containers/Projects/Projects.selectors";
import {
    forceRecordUpdate,
    migrateDeprecationsKey,
} from "containers/Resources/components/CommonActions/CommonActions";
import { commitActionSignature } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/CommitAction";
import { UncommittedDataGroupedByName } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { SaveSplitButton } from "containers/Resources/screens/ResourcesSingle/components/RecordActionButtons/SaveSplitButton";
import { ShownActionButton } from "containers/Resources/screens/ResourcesSingle/components/RecordActionButtons/ShownActionButton";
import { schedulerKey } from "containers/Resources/screens/ResourcesSingle/constants";
import { useMigrateOnSaveParams } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import { saveNestedForms } from "containers/Resources/screens/ResourcesSingle/utils";
import { AnyObject, FormApi } from "final-form";
import { Bundle, Plugin_Action, Record } from "graphql/generated/graphql";
import qs from "qs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useMount } from "react-use";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import { capitalizeFirstLetterRestLowercase, resourceList } from "utils/utils";
import "./RecordActionButtons.scss";

interface PluginActionWithChildren {
    id: Plugin_Action["id"];
    parentId: Plugin_Action["parent_plugin_action_id"];
    children: PluginActionWithChildren[];
    actionObj: Plugin_Action;
}

interface RecordActionButtonProps {
    submitForm: () => Promise<AnyObject>;
    resourceData?: Record;
    resourceTypeName: FeConfigType;
    enableSave: boolean;
    isLoading: boolean;
    fetchResourceData: () => void;
    formPristine: boolean;
    hasValidationErrors?: boolean;
    nestedFormSaveData?: { [key: string]: () => any };
    isNewRecord?: boolean;
    defaultValues?: any;
    errorMessage?: string;
    form?: FormApi;
    canCommitOnSaveDefaultValue: boolean;
    setDirtyForm?: () => void;
    uncommittedDataByPath?: UncommittedDataGroupedByName;
    closeForm?: () => void;
    isGitLocked?: boolean;
    bootstrapUserName?: string;
    iri?: string;
}

const RecordActionButtons = ({
    submitForm,
    resourceData,
    resourceTypeName,
    enableSave,
    isLoading,
    fetchResourceData,
    formPristine,
    hasValidationErrors,
    nestedFormSaveData = {},
    isNewRecord,
    defaultValues,
    errorMessage,
    form,
    canCommitOnSaveDefaultValue,
    setDirtyForm,
    uncommittedDataByPath = {},
    closeForm,
    isGitLocked,
    bootstrapUserName,
    iri,
}: RecordActionButtonProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const queryParams = qs.parse(history.location.search.substring(1));
    const resourceName = history.location.pathname?.split("/")[1] as FeConfigType;
    const resourceConfig = getRecordConfig(resourceName);
    const resourceActions = resourceConfig?.actions || {};
    const activeProjectId = useSelector(getActiveOrDefaultProjectId);
    const userData = useSelector(getUserData);

    const permissionData = useSelector(getUserPermissionData);
    const canUserCreateUser = hasCreateUserPermission(permissionData);
    const isReadOnly = getIsReadonly(resourceConfig, resourceData);
    const isDeleted = resourceData?.deleted_at != null;

    const plugins = useSelector(getPlugins);

    const { pluginActionParents, missingParentWarnings } = useMemo(() => {
        const pluginActions: PluginActionWithChildren[] = [];
        const missingParentWarnings: { parentId: string; childId: string }[] = [];

        plugins.forEach((plugin) => {
            plugin?.plugin_action_plain_list_plugin_id?.forEach((pluginAction: Plugin_Action) => {
                if (
                    resourceTypeName !== pluginAction?.record_type?.toLowerCase() ||
                    pluginAction?.type !== "RECORD"
                )
                    return;

                const pushPluginActions = () =>
                    pluginActions.push({
                        id: pluginAction?.id,
                        parentId: pluginAction?.parent_plugin_action_id,
                        children: [],
                        actionObj: pluginAction,
                    });

                if (pluginAction?.record_filter) {
                    const matchesFilter = Object.entries(pluginAction.record_filter).some(
                        ([key, value]) =>
                            (resourceData && resourceData[key] === value) ||
                            (isNewRecord &&
                                (queryParams?.connector_type === value ||
                                    defaultValues?.["connector_type"] === value))
                    );
                    if (matchesFilter) {
                        pushPluginActions();
                    }
                } else {
                    pushPluginActions();
                }
            });
        });

        pluginActions.forEach((action) => {
            if (!action.parentId) return;
            const parentAction = pluginActions.find(
                (otherAction) => otherAction.id === action.parentId
            );
            if (!parentAction) {
                missingParentWarnings.push({
                    parentId: action.parentId,
                    childId: action.id,
                });
                return;
            }
            parentAction.children.push(action);
        });

        return {
            pluginActionParents: pluginActions.filter((action) => action.parentId == null),
            missingParentWarnings,
        };
    }, [plugins, resourceData, resourceTypeName, isNewRecord, queryParams, defaultValues]);

    const warnedMissingParentsRef = useRef(new Set<string>());
    useEffect(() => {
        missingParentWarnings.forEach(({ parentId, childId }) => {
            const key = `${parentId}:${childId}`;
            if (warnedMissingParentsRef.current.has(key)) {
                return;
            }
            warnedMissingParentsRef.current.add(key);
            NotificationService.error(
                <SimpleNotification
                    title="Failed to load plugin action."
                    subtitle={`Could not find parent plugin action "${parentId}" for child "${childId}".`}
                />
            );
        });
    }, [missingParentWarnings]);

    const saveNestedFormsWithData = useCallback(
        () => saveNestedForms(nestedFormSaveData),
        [nestedFormSaveData]
    );

    const saveForm = useCallback(
        () => saveNestedFormsWithData().then(() => submitForm()),
        [saveNestedFormsWithData, submitForm]
    );

    const shownActionList = useMemo(() => {
        const shownActionList = resourceActions?.shown.filter(
            (action) => action.key !== migrateDeprecationsKey
        );
        if (
            resourceData &&
            resourceList.some((resource) => resource === resourceTypeName.toUpperCase())
        ) {
            shownActionList.unshift(commitActionSignature);
        }

        return shownActionList;
    }, [resourceActions?.shown, resourceData, resourceTypeName]);

    const shownMigrationAction = useMemo(() => {
        return resourceActions?.shown.find((action) => action.key === migrateDeprecationsKey);
    }, [resourceActions?.shown]);

    const isSaveButtonDisabled =
        hasValidationErrors || (isNewRecord ? !enableSave : !enableSave || isReadOnly || isDeleted);

    const saveFormWithCommit = (canCommit) => {
        if (form?.mutators?.changeCanCommitOnSave) {
            form?.mutators
                ?.changeCanCommitOnSave(canCommit)
                .then(() => saveForm())
                .then(() => form?.mutators?.changeCanCommitOnSave(canCommitOnSaveDefaultValue));
        } else {
            saveForm();
        }
    };

    const bundleState: BundleState[] = useSelector(
        (state: any) => state?.dataStore?.bundle_state ?? []
    );
    const currentBundleState = useMemo(
        () =>
            resourceTypeName === "bundle"
                ? bundleState.findLast(
                      ({ bundle_key }) => bundle_key === (resourceData as Bundle)?.key
                  )
                : undefined,
        [bundleState, resourceData, resourceTypeName]
    );

    const NotificationWithLogLink = (text, link) => (
        <>
            {`${text}`}. Please inspect the{" "}
            <Button
                onClick={() => history.push(link)}
                label="logs"
                variation="link-light"
                className="notificationLink"
            />
            .
        </>
    );

    const [isMounted, setMounted] = useState(false);

    useMount(() => setMounted(true));

    useEffect(() => {
        if (currentBundleState?.operation && isMounted) {
            const workspaceLogsLink = `${mapScreensToPaths.bundleManager(
                userData?.workspace_id
            )}#${logScreenId}`;
            const notificationText = `${capitalizeFirstLetterRestLowercase(
                currentBundleState.operation
            )} of "${resourceData?.name}" ${currentBundleState.state.toLowerCase()}`;
            if (currentBundleState?.state === "FAILED") {
                NotificationService.error(
                    NotificationWithLogLink(notificationText, workspaceLogsLink)
                );
            } else if (currentBundleState?.state === "SUCCEEDED") {
                if (currentBundleState?.operation === "UNINSTALL") {
                    NotificationService.info(
                        NotificationWithLogLink(notificationText, workspaceLogsLink)
                    );
                } else {
                    NotificationService.success(notificationText);
                    forceRecordUpdate(dispatch);
                }
            } else if (currentBundleState?.state === "STARTED") {
                NotificationService.info(notificationText);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBundleState?.operation, currentBundleState?.state]);

    const bundleResponseData: BundleResponseData = useSelector(getBundlesData);
    const remoteBundleVersion = useMemo(() => {
        if (
            resourceName === "bundle" &&
            Object.values(bundleResponseData).length &&
            (resourceData as Bundle)?.bundle_repository_id_bundle_repository
        ) {
            const bundleRepositoryId = (resourceData as Bundle)
                .bundle_repository_id_bundle_repository?.id as string;
            const remoteBundleVersion = bundleResponseData[bundleRepositoryId]?.bundle_list.find(
                (bundle) => bundle.id === resourceData?.id
            )?.version;
            return remoteBundleVersion;
        }
    }, [bundleResponseData, resourceData, resourceName]);

    const provisioningState = useSelector(
        singleCustomObjectProvisioningStateSelector(resourceData?.id)
    );

    const commitParams = { commit: canCommitOnSaveDefaultValue };
    const migrateOnSaveParams = useMigrateOnSaveParams();

    const schedulerList = useSelector(
        (state: any) => state?.dataStore?.[schedulerKey]
    )?.scheduler_plain_list;

    const singleActionProps = (action) => {
        return {
            action,
            actionData: {
                resourceType: resourceName,
                resourceData,
                iri,
                activeProjectId,
                userData,
                isReadOnly,
                afterAction: action?.hasAfterAction ? fetchResourceData : undefined,
                uncommittedData: uncommittedDataByPath,
                setDirtyForm,
                currentBundleState,
                remoteBundleVersion,
                saveAdditionalParams: { ...commitParams, ...migrateOnSaveParams },
                schedulerList: resourceName === "flow" ? schedulerList : undefined,
                canUserCreateUser,
                isGitLocked,
                provisioningState,
                bootstrapUserName,
            },
            hasValidationErrors,
            formPristine,
            resourceName,
            isLoading,
            saveForm,
            fetchResourceData,
            saveNestedFormsWithData,
        };
    };

    return (
        <div className="recordActionButtons">
            {resourceConfig?.form && (
                <>
                    {canCommitOnSaveDefaultValue === false ||
                    resourceData?.track_in_git === false ||
                    (isNewRecord && form?.getState().values?.track_in_git === false) ? (
                        <Tooltip overlay="Ctrl+S">
                            <Button
                                icon={IconCollection.saveOutline}
                                label="Save"
                                variation="dark"
                                onClick={saveForm}
                                disabled={
                                    hasValidationErrors ||
                                    (isNewRecord
                                        ? !enableSave
                                        : !enableSave || isReadOnly || isDeleted)
                                }
                                loading={isLoading}
                            />
                        </Tooltip>
                    ) : (
                        <SaveSplitButton
                            saveFormWithCommit={saveFormWithCommit}
                            icon={IconCollection.saveOutline}
                            isDisabled={isSaveButtonDisabled}
                            isLoading={isLoading}
                            canCommit={canCommitOnSaveDefaultValue}
                        />
                    )}

                    {errorMessage && (
                        <span className="recordActionButtons-errorMessage">{errorMessage}</span>
                    )}
                </>
            )}
            {isNewRecord && closeForm ? (
                <CancelButton onClick={closeForm} variation="dark" />
            ) : (
                <>
                    {resourceName &&
                        resourceData &&
                        shownActionList?.map((action) => (
                            <ShownActionButton key={action.key} {...singleActionProps(action)} />
                        ))}

                    {pluginActionParents.map((action) => (
                        <PluginListAction
                            parentAction={action.actionObj}
                            childActions={action.children.map((a) => a.actionObj)}
                            resourceIds={[resourceData?.id]}
                            isSingularAction
                            afterAction={fetchResourceData}
                            key={`PluginListAction ${action.id}`}
                            isDeleted={isDeleted}
                            {...(formPristine ? {} : { beforeAction: saveForm })}
                        />
                    ))}
                    {resourceName && resourceData && shownMigrationAction && (
                        <ShownActionButton
                            key={shownMigrationAction.key}
                            {...singleActionProps(shownMigrationAction)}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default RecordActionButtons;
